import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Wordmark } from '@/components/ui/Wordmark';
import { isApiError } from '@/lib/api/errors';
import { signIn } from '@/services/authService';
import { CodeSignIn } from './CodeSignIn';

const schema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
  stayLoggedIn: z.boolean(),
});
type Form = z.infer<typeof schema>;

export function SignInPage() {
  const navigate = useNavigate();
  const [codeFor, setCodeFor] = useState<string | null>(null); // email, once "Email me a code" is chosen
  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', stayLoggedIn: false },
  });
  const login = useMutation({
    mutationFn: (v: Form) => signIn(v.email.trim(), v.password, v.stayLoggedIn),
    meta: { silent: true }, // shown inline below
    onSuccess: () => navigate({ to: '/' }),
  });

  const error = login.error
    ? isApiError(login.error) && (login.error.status === 401 || login.error.status === 400)
      ? 'That email and password do not match.'
      : 'Sign-in is unavailable right now. Try again shortly.'
    : null;

  if (codeFor !== null) {
    return (
      <div className="flex min-h-full items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl bg-surface p-8 shadow-sm">
          <img src="/simons-logo.png" alt="Simons Real Estate Group" className="h-10 w-auto" />
          <h1 className="mb-6 mt-6 text-sm text-ink-soft">
            Sign in to <Wordmark className="text-base" /> with an emailed code
          </h1>
          <CodeSignIn
            email={codeFor}
            stayLoggedIn={form.getValues('stayLoggedIn')}
            onSignedIn={() => void navigate({ to: '/' })}
          />
          <button
            type="button"
            onClick={() => setCodeFor(null)}
            className="mt-6 w-full text-xs font-semibold text-ink-faint hover:text-ink"
          >
            Use my password instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <form
        onSubmit={form.handleSubmit((v) => login.mutate(v))}
        className="w-full max-w-sm rounded-2xl bg-surface p-8 shadow-sm"
        noValidate
      >
        <img src="/simons-logo.png" alt="Simons Real Estate Group" className="h-10 w-auto" />
        <h1 className="mb-8 mt-6 text-sm text-ink-soft">
          Sign in to <Wordmark className="text-base" />
        </h1>
        <label className="block text-sm font-semibold text-ink" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          {...form.register('email')}
          className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-sage"
        />
        {form.formState.errors.email && (
          <p className="mt-1 text-xs text-bronze">{form.formState.errors.email.message}</p>
        )}

        <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          {...form.register('password')}
          className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-sage"
        />
        {form.formState.errors.password && (
          <p className="mt-1 text-xs text-bronze">{form.formState.errors.password.message}</p>
        )}

        <label className="mt-4 flex items-center gap-2 text-sm text-ink-soft">
          <input type="checkbox" {...form.register('stayLoggedIn')} className="accent-sage" />
          Keep me signed in for two weeks
        </label>

        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-surface-sunken px-3 py-2 text-sm text-bronze">
            {error}
          </p>
        )}

        <Button type="submit" className="mt-6 w-full" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>
        <button
          type="button"
          onClick={async () => {
            // Needs only a valid email, not a password.
            if (await form.trigger('email')) setCodeFor(form.getValues('email').trim());
          }}
          className="mt-3 w-full text-sm font-semibold text-sage hover:underline"
        >
          Email me a sign-in code instead
        </button>
        <p className="mt-6 text-center text-xs text-ink-faint">Simons REG private workspace</p>
      </form>
    </div>
  );
}
